import React, { useMemo, useState, PropsWithChildren } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Tabs,
  Tab,
  Paper,
  Checkbox,
  MenuItem,
  Select,
} from "@material-ui/core";
import ApartmentIcon from "@material-ui/icons/Apartment";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import HomeWork from "@material-ui/icons/HomeWork";

import { MUTATIONS, QUERIES } from "../../../../graphql";
import {
  Apartment,
  ApartmentDataService,
  ApartmentOwner,
  ApartmentWiserOpts,
  Building,
  CreateApartmentData,
  DomoticsProvider,
  User,
  ApartmentOnboarding,
} from "../../../../graphql/models";

import useApartmentFormStyle from "./apartment_form.style";

import {
  ApartmentElectronicsOptionsEditor,
  ApartmentGuestsEditor,
  ApartmentRoomsEditor,
  ApartmentServicesEditor,
  GqlRecordPicker,
} from "../../../components";
import { useSnackbarMutation } from "../../../../hooks";
import { ApartmentOnboardingComponent } from "../../../components/apartment_onboarding";

// --- Tab Panel definition
interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`apartment-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export interface ApartmentEditFormProps extends PropsWithChildren<any> {
  value: Apartment | CreateApartmentData | undefined;
  onValueUpdatedSuccessfully: (v: Apartment) => void;
}

export function ApartmentForm(props: ApartmentEditFormProps) {
  const { value, onValueUpdatedSuccessfully } = props;
  const classes = useApartmentFormStyle();

  const isEditMode = useMemo(() => {
    return (value as Apartment)?.id;
  }, [value]);

  const gqlMutation = useMemo(() => {
    return isEditMode ? MUTATIONS.UPDATE_APARTMENT : MUTATIONS.CREATE_APARTMENT;
  }, [isEditMode]);

  const [selectedTabPanelIndex, setSelectedTabPanelIndex] = useState<number>(0);
  const handleSelectedTabPanelIndexChange = (evt, newValue) =>
    setSelectedTabPanelIndex(newValue);

  const [
    invokeCreateOrUpdateApartment,
    { loading, data: createOrUpdateApartmentData },
  ] = useSnackbarMutation(gqlMutation);
  const [updated, setUpdated] = useState(false);

  // Beware: since name seems to be unncessary, they decided to use it as the
  // "notes" field.
  const [name, setName] = useState<string>(value?.name ?? "");
  const [code, setCode] = useState<string>(value?.code ?? "");
  const [building, setBuilding] = useState<Building | undefined>(
    value?.building ?? void 0
  );
  const [owner, setOwner] = useState<User | ApartmentOwner | undefined>(
    value?.owner ?? void 0
  );
  const [reservationOptions, setReservationOptions] = useState<
    ApartmentDataService[]
  >(value?.reservationOptions ?? []);
  const [domoticsProvider, setDomoticsProvider] = useState<
    DomoticsProvider | undefined
  >((value as Apartment)?.domoticsProvider ?? void 0);
  const [wiserOpts, setWiserOpts] = useState<ApartmentWiserOpts | undefined>(
    (value as Apartment)?.wiserOpts ?? void 0
  );
  const [underConstruction, setUnderConstruction] = useState<boolean>(
    value?.underConstruction ?? false
  );
  const [onboarding, setOnboarding] = useState<ApartmentOnboarding>(
    value?.onboarding ?? {}
  );
  const [floorInfo, setFloorInfo] = useState(value?.floorInfo ?? "");
  const [tag, setTag] = useState(value?.tag ?? "");

  const setOnboardingChange = (value: ApartmentOnboarding) => {
    setOnboarding(value);
    setSelectedTabPanelIndex(0);
  };

  const onElectronicsOptionsChange = (apartment: Apartment) => {
    // console.log('Apartment.wiserOpts is', {...apartment.wiserOpts});
    setDomoticsProvider(apartment.domoticsProvider);
    setWiserOpts({ ...apartment.wiserOpts });
  };

  const electronicsApartment = useMemo(() => {
    return {
      ...(value as Apartment),
      domoticsProvider,
      wiserOpts,
    };
  }, [value, domoticsProvider, wiserOpts]);

  const ownerName = useMemo(() => {
    let name;
    if (owner) {
      const ownerAsApartmentOwner = owner as ApartmentOwner;
      if (ownerAsApartmentOwner.name) {
        name = ownerAsApartmentOwner?.name;
      } else {
        const ownerAsUser = owner as User;
        name = [ownerAsUser.firstName, ownerAsUser.lastName].join(" ");
      }
    }

    return name;
  }, [owner]);

  const isD18 = useMemo(() => code.startsWith("D18"), [code]);

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    invokeCreateOrUpdateApartment({
      variables: {
        id: (value as Apartment)?.id,
        name: name ?? "-", // fix GQL Error: required field
        code,
        building: building
          ? {
              id: building.id,
              name: building.name,
              code: building.code,
            }
          : void 0,
        owner: owner
          ? {
              id: owner.id,
              name: ownerName,
              email: owner.email,
              phone: owner.phone,
            }
          : void 0,
        reservationOptions,
        domoticsProvider,
        wiserOpts,
        underConstruction,
        onboarding,
        floorInfo,
        tag,
      },
    });
  };

  if (createOrUpdateApartmentData && !updated) {
    onValueUpdatedSuccessfully(
      createOrUpdateApartmentData.createSystemApartment
    );
    setUpdated(true);
  }

  return (
    <>
      {loading && `Loading...`}
      {!createOrUpdateApartmentData && (
        <>
          <Tabs
            value={selectedTabPanelIndex}
            onChange={handleSelectedTabPanelIndexChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            aria-label="Dettaglio Appartmento"
          >
            <Tab
              id={`apartment-tab-0`}
              icon={<ApartmentIcon />}
              disabled={!isEditMode}
              label="Informazioni Appartamento"
            />
            <Tab
              id={`apartment-tab-1`}
              disabled={!isEditMode}
              icon={<MeetingRoomIcon />}
              label="Stanze e Dispositivi"
            />
            <Tab
              id={`apartment-tab-2`}
              disabled={!isEditMode}
              icon={<PeopleAltIcon />}
              label={"Ospiti"}
            />
            <Tab
              id={"tab-onboarding"}
              disabled={true || !isEditMode || !underConstruction}
              icon={<HomeWork />}
              label="Onboarding"
            />
          </Tabs>
          <TabPanel value={selectedTabPanelIndex} index={0}>
            <form
              className={classes.root}
              onSubmit={onFormSubmit}
              noValidate
              autoComplete="off"
            >
              <Grid container spacing={3}>
                <Grid item sm={4} xs={12}>
                  <TextField
                    required
                    className={classes.textField}
                    label="Codice"
                    variant="outlined"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <GqlRecordPicker
                    required
                    disabled={!!isEditMode}
                    className={classes.textField}
                    multiple={false}
                    value={building}
                    getOptionLabel={(record) => record.name}
                    placeHolder={"Palazzo"}
                    renderOption={(record) => record.name}
                    onValueChange={(value) => setBuilding(value)}
                    query={QUERIES.BUILDINGS("light")}
                    queryResultDataField={"buildings"}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <GqlRecordPicker
                    className={classes.textField}
                    multiple={false}
                    value={owner}
                    getOptionLabel={(record) => record.name ?? record.username}
                    placeHolder={"Proprietario"}
                    renderOption={(record) => record.name ?? record.username}
                    onValueChange={(value) => setOwner(value)}
                    query={QUERIES.USERS}
                    queryResultDataField={"users"}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <TextField
                    className={classes.textField}
                    label="Note appartamento (es. scala, ecc)"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <Typography variant="subtitle1">
                    <Checkbox
                      checked={underConstruction}
                      onChange={(_evt, checked) =>
                        setUnderConstruction(!underConstruction)
                      }
                    />
                    Abilita OnBoarding (gestione su nuovo BO)
                  </Typography>
                </Grid>
                <Grid item sm={4} xs={12}>
                  {isD18 ? (
                    <TextField
                      className={classes.textField}
                      label="Membership (Duomo 18)"
                      variant="outlined"
                      disabled
                      value={tag}
                      onChange={(e) => setTag(e.target.value)}
                    />
                  ) : (
                    <Select
                      label="Piano (Bevy)"
                      variant="outlined"
                      displayEmpty
                      className={classes.textField}
                      value={floorInfo}
                      onChange={(evt: any) =>
                        setFloorInfo(evt.target.value.trim())
                      }
                    >
                      <MenuItem value={undefined} disabled>
                        -
                      </MenuItem>
                      <MenuItem
                        key="Piano terra/Piano rialzato"
                        value="Piano terra/Piano rialzato"
                      >
                        Piano terra/Piano rialzato
                      </MenuItem>
                      <MenuItem key="Primo piano" value="Primo piano">
                        Primo piano
                      </MenuItem>
                      <MenuItem key="Secondo piano" value="Secondo piano">
                        Secondo piano
                      </MenuItem>
                      <MenuItem
                        key="Terzo piano o superiore"
                        value="Terzo piano o superiore"
                      >
                        Terzo piano o superiore
                      </MenuItem>
                    </Select>
                  )}
                </Grid>
                {building && (
                  <Grid item sm={12} xs={12}>
                    <ApartmentServicesEditor
                      buildingId={building.id}
                      apartmentServices={reservationOptions}
                      apartmentServicesChange={setReservationOptions}
                    />
                  </Grid>
                )}
                <Grid item sm={12} xs={12}>
                  <ApartmentElectronicsOptionsEditor
                    apartment={electronicsApartment}
                    onChange={onElectronicsOptionsChange}
                  />
                </Grid>
                <Grid item sm={12} xs={12}>
                  <Button
                    className={classes.textField}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    {!loading ? "Salva" : <CircularProgress />}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </TabPanel>
          <TabPanel value={selectedTabPanelIndex} index={1}>
            <Grid item sm={12} xs={12}>
              <ApartmentRoomsEditor apartment={value as Apartment} />
            </Grid>
          </TabPanel>
          <TabPanel value={selectedTabPanelIndex} index={2}>
            <Paper elevation={3} style={{ padding: "1rem" }}>
              <ApartmentGuestsEditor apartment={(value as Apartment).id} />
            </Paper>
          </TabPanel>
          <TabPanel value={selectedTabPanelIndex} index={3}>
            <div>
              <Typography variant="h2">Documenti e contratti</Typography>
              <Paper style={{ padding: "1rem" }}>
                <ApartmentOnboardingComponent
                  onboarding={onboarding}
                  onChange={setOnboardingChange}
                />
              </Paper>
            </div>
          </TabPanel>
        </>
      )}
    </>
  );
}
